import {
  AudioLines,
  BellRing,
  CalendarClock,
  CalendarDays,
  Contact,
  Database,
  FileSearch,
  FileSpreadsheet,
  Fingerprint,
  Globe2,
  History,
  ListFilter,
  type LucideIcon,
  MapPin,
  MapPinned,
  MessageSquareText,
  MessagesSquare,
  Navigation,
  Network,
  ScanFace,
  Search,
  ShieldCheck,
  Sparkles,
  Speech,
  UserRoundSearch,
} from 'lucide-react'
import type { ComponentType } from 'react'
import { SchemaAgentIntroIllustration } from '@/features/schema-agent/SchemaAgentIntroIllustration'
import { FaceMatchingIllustration } from './illustrations/FaceMatchingIllustration'
import { HumintWorkspaceIllustration } from './illustrations/HumintWorkspaceIllustration'
import { LongHorizonFileIllustration } from './illustrations/LongHorizonFileIllustration'
import { MapAddressSearchIllustration } from './illustrations/MapAddressSearchIllustration'
import { MobileForensicsIllustration } from './illustrations/MobileForensicsIllustration'
import { OsintAgentIllustration } from './illustrations/OsintAgentIllustration'
import { StructuredIngestIllustration } from './illustrations/StructuredIngestIllustration'
import { StructuredSearchIllustration } from './illustrations/StructuredSearchIllustration'
import { VoiceMatchingIllustration } from './illustrations/VoiceMatchingIllustration'

export interface FeatureSlide {
  id: string
  labelKey: string
  contentKey: string
  Illustration: ComponentType
  benefits: readonly { key: string; icon: LucideIcon }[]
}

/** Add a slide here, its illustration, and translated copy; the carousel needs no changes. */
export const featureSlides = [
  {
    id: 'structured-ingest',
    labelKey: 'whatsNew.slides.structuredIngest.label',
    contentKey: 'whatsNew.slides.structuredIngest',
    Illustration: StructuredIngestIllustration,
    benefits: [
      { key: 'files', icon: FileSpreadsheet },
      { key: 'mapping', icon: Sparkles },
      { key: 'control', icon: ShieldCheck },
    ],
  },
  {
    id: 'schema-agent',
    labelKey: 'schemaAgent.title',
    contentKey: 'schemaAgent.intro',
    Illustration: SchemaAgentIntroIllustration,
    benefits: [
      { key: 'files', icon: FileSearch },
      { key: 'discuss', icon: MessageSquareText },
      { key: 'control', icon: ShieldCheck },
    ],
  },
  {
    id: 'mobile-forensics',
    labelKey: 'whatsNew.slides.mobileForensics.label',
    contentKey: 'whatsNew.slides.mobileForensics',
    Illustration: MobileForensicsIllustration,
    benefits: [
      { key: 'conversations', icon: MessageSquareText },
      { key: 'activity', icon: MapPin },
      { key: 'evidence', icon: ShieldCheck },
    ],
  },
  {
    id: 'osint-agent',
    labelKey: 'whatsNew.slides.osintAgent.label',
    contentKey: 'whatsNew.slides.osintAgent',
    Illustration: OsintAgentIllustration,
    benefits: [
      { key: 'sources', icon: Globe2 },
      { key: 'connections', icon: Network },
      { key: 'review', icon: Search },
    ],
  },
  {
    id: 'structured-search',
    labelKey: 'whatsNew.slides.structuredSearch.label',
    contentKey: 'whatsNew.slides.structuredSearch',
    Illustration: StructuredSearchIllustration,
    benefits: [
      { key: 'ask', icon: MessageSquareText },
      { key: 'query', icon: ListFilter },
      { key: 'trace', icon: Database },
    ],
  },
  {
    id: 'map-address-search',
    labelKey: 'whatsNew.slides.mapAddressSearch.label',
    contentKey: 'whatsNew.slides.mapAddressSearch',
    Illustration: MapAddressSearchIllustration,
    benefits: [
      { key: 'suggestions', icon: Search },
      { key: 'navigation', icon: Navigation },
      { key: 'temporary', icon: MapPinned },
    ],
  },
  {
    id: 'face-matching',
    labelKey: 'whatsNew.slides.faceMatching.label',
    contentKey: 'whatsNew.slides.faceMatching',
    Illustration: FaceMatchingIllustration,
    benefits: [
      { key: 'detect', icon: ScanFace },
      { key: 'match', icon: UserRoundSearch },
      { key: 'control', icon: ShieldCheck },
    ],
  },
  {
    id: 'voice-matching',
    labelKey: 'whatsNew.slides.voiceMatching.label',
    contentKey: 'whatsNew.slides.voiceMatching',
    Illustration: VoiceMatchingIllustration,
    benefits: [
      { key: 'transcript', icon: MessagesSquare },
      { key: 'language', icon: Speech },
      { key: 'voices', icon: AudioLines },
    ],
  },
  {
    id: 'humint-workspace',
    labelKey: 'whatsNew.slides.humintWorkspace.label',
    contentKey: 'whatsNew.slides.humintWorkspace',
    Illustration: HumintWorkspaceIllustration,
    benefits: [
      { key: 'subject', icon: Contact },
      { key: 'encounters', icon: CalendarDays },
      { key: 'evidence', icon: Fingerprint },
    ],
  },
  {
    id: 'long-horizon-file',
    labelKey: 'whatsNew.slides.longHorizonFile.label',
    contentKey: 'whatsNew.slides.longHorizonFile',
    Illustration: LongHorizonFileIllustration,
    benefits: [
      { key: 'watch', icon: BellRing },
      { key: 'history', icon: History },
      { key: 'asOf', icon: CalendarClock },
    ],
  },
] as const satisfies readonly [FeatureSlide, ...FeatureSlide[]]
